import { useEffect, useState } from 'react'
import { Loader2, Sparkles } from 'lucide-react'
import { listCards, recommend, type Card, type RecommendResponse } from '../lib/api'

const SUGGESTED = [
  { amount: 120, category: 'dining' },
  { amount: 200, category: 'groceries' },
  { amount: 60, category: 'gas' },
  { amount: 800, category: 'travel' },
  { amount: 400, category: 'online' },
]

export function Recommend() {
  const [amount, setAmount] = useState('')
  const [category, setCategory] = useState('')
  const [result, setResult] = useState<RecommendResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [activeCards, setActiveCards] = useState<Card[]>([])

  useEffect(() => {
    listCards().then((r) => setActiveCards(r.cards.filter((c) => c.status === 'active')))
  }, [])

  const run = async (a: number, c: string) => {
    setLoading(true)
    setErr(null)
    try {
      const r = await recommend(a, c)
      setResult(r)
    } catch (e: any) {
      setErr(e?.message || 'failed')
    } finally {
      setLoading(false)
    }
  }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const a = parseFloat(amount)
    if (!a || !category) return
    run(a, category)
  }

  if (activeCards.length === 0) {
    return (
      <div className="space-y-6 max-w-2xl">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 flex items-center gap-2">
            <Sparkles size={12} /> recommend
          </div>
          <h1 className="text-2xl font-light text-zinc-100 mt-1">best card for…</h1>
        </div>
        <div className="border border-dashed border-zinc-900 rounded px-6 py-12 text-center">
          <div className="text-zinc-400 text-sm mb-1">no active cards yet</div>
          <div className="text-zinc-600 text-xs">
            add some at <a href="/cards" className="text-zinc-300 underline">/cards</a> — recommendations
            need a portfolio to rank against.
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8 max-w-3xl">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-zinc-500 flex items-center gap-2">
          <Sparkles size={12} /> recommend
        </div>
        <h1 className="text-2xl font-light text-zinc-100 mt-1">best card for…</h1>
        <p className="text-zinc-500 text-sm mt-2 leading-relaxed">
          Ranks your {activeCards.length} active card{activeCards.length === 1 ? '' : 's'} by total
          value: <span className="text-zinc-400">amount × multiplier × cpp</span> plus marginal SUB
          credit when an open sign-up bonus would benefit. cpp values come from per-tenant overrides
          in <code className="text-zinc-400">money_engine.point_balances</code>, falling back to TPG
          monthly valuations.
        </p>
      </div>

      <form onSubmit={onSubmit} className="flex items-center gap-2">
        <span className="text-zinc-600 text-base">$</span>
        <input
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputMode="decimal"
          placeholder="amount"
          className="w-32 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100 num focus:outline-none focus:border-zinc-600"
          autoFocus
        />
        <input
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          placeholder="category (dining, gas, travel, online, …)"
          className="flex-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-zinc-600"
        />
        <button
          type="submit"
          disabled={loading || !amount || !category}
          className="px-4 py-2 rounded bg-zinc-100 text-zinc-900 text-sm font-medium hover:bg-white disabled:opacity-40"
        >
          {loading ? '…' : 'rank'}
        </button>
      </form>

      <div>
        <div className="text-[11px] uppercase tracking-wider text-zinc-600 mb-2">try</div>
        <div className="flex flex-wrap gap-2">
          {SUGGESTED.map((s) => (
            <button
              key={`${s.amount}-${s.category}`}
              onClick={() => {
                setAmount(String(s.amount))
                setCategory(s.category)
                run(s.amount, s.category)
              }}
              className="px-3 py-1.5 rounded border border-zinc-800 hover:border-zinc-600 hover:bg-zinc-900/50 text-zinc-400 hover:text-zinc-200 text-xs transition-colors"
            >
              ${s.amount} {s.category}
            </button>
          ))}
        </div>
      </div>

      {err && <div className="text-red-400 text-sm">{err}</div>}

      {loading && (
        <div className="flex items-center gap-2 text-zinc-600 text-sm">
          <Loader2 className="animate-spin" size={14} /> ranking…
        </div>
      )}

      {result && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-zinc-600 mb-2">
            ${result.amount.toLocaleString()} · {result.category}
          </div>
          {result.ranked.length === 0 ? (
            <div className="border border-dashed border-zinc-900 rounded px-4 py-6 text-center text-zinc-600 text-sm">
              no cards matched.
            </div>
          ) : (
            <ol className="space-y-2">
              {result.ranked.map((r, idx) => (
                <li
                  key={r.card_id}
                  className={
                    'flex items-start justify-between border rounded px-5 py-4 transition-colors ' +
                    (idx === 0
                      ? 'border-emerald-700/60 bg-emerald-950/20'
                      : 'border-zinc-900 hover:border-zinc-800')
                  }
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={
                        'text-xs num w-6 h-6 rounded-full flex items-center justify-center ' +
                        (idx === 0
                          ? 'bg-emerald-600/20 text-emerald-400 border border-emerald-700/50'
                          : 'bg-zinc-900 text-zinc-500')
                      }
                    >
                      {idx + 1}
                    </div>
                    <div>
                      <div className="text-zinc-100 text-sm">{r.name}</div>
                      <div className="text-[11px] text-zinc-500 mt-1">{r.reasoning}</div>
                      {r.portal_only && (
                        <div className="text-[10px] text-amber-500 mt-1">
                          portal-only — must redeem through issuer's portal
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-right num">
                    <div className="text-zinc-100 text-base">${r.total_value.toFixed(2)}</div>
                    <div className="text-[10px] text-zinc-600 mt-0.5">
                      ${r.reward_dollars.toFixed(2)} earn
                      {r.sub_dollars > 0 && (
                        <span className="text-emerald-500"> + ${r.sub_dollars.toFixed(2)} SUB</span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  )
}
